import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import {
  ArrowLeft, RefreshCw, Search, Upload, Trash2, Plus, ChevronLeft, ChevronRight,
  AlertCircle, CheckCircle2, FileText, Package, ChefHat, Receipt, Download, ListChecks, MapPinned, Flame,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import TDBCostEntriesTab from '@/components/tdb/TDBCostEntriesTab';
import TDBCostByCityTab from '@/components/tdb/TDBCostByCityTab';
import TDBCostIntelligence from '@/components/TDBCostIntelligence';
import TDBLeaderCockpit from '@/components/TDBLeaderCockpit';

const fC = (v) => {
  const n = Number(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1).replace(/\.?0+$/, '')}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const STATUS_COLORS = {
  green: { bg: '#ECFDF5', text: '#065F46', accent: '#2D8B5E', label: 'On target' },
  amber: { bg: '#FEF3C7', text: '#92400E', accent: '#BA7517', label: 'Watch (>15%)' },
  red:   { bg: '#FEF2F2', text: '#991B1B', accent: '#D4726A', label: 'Over budget (>20%)' },
  unset: { bg: '#F3F4F6', text: '#6B7280', accent: '#9CA3AF', label: 'No recipe yet' },
};

const TABS = [
  { id: 'intelligence', label: 'Cost Intelligence', icon: Flame },
  { id: 'cost-entries', label: 'Cost Entries', icon: ListChecks },
  { id: 'by-city', label: 'By City', icon: MapPinned },
  { id: 'pnl', label: 'Monthly P&L', icon: ChefHat },
  { id: 'products', label: 'Products & Recipes', icon: Package },
  { id: 'invoices', label: 'Invoice Scanner', icon: Receipt },
];

export default function TDBCostModule() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('intelligence');

  const hasAccess = user && (user.role === 'ceo' ||
    user.division_id === 'tdb' ||
    (user.additional_division_ids || []).includes('tdb') ||
    user.accounts_cross_division);

  if (!hasAccess) {
    return (
      <div className="text-center py-16">
        <p className="text-sm" style={{ color: '#9CA3AF' }}>TDB Cost Module — access restricted.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-5 px-2 sm:px-4" data-testid="tdb-cost-module">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs hover:text-[#2A7B88]" style={{ color: '#9CA3AF' }}>
        <ArrowLeft size={14} /> Back
      </button>
      <div>
        <h1 className="text-xl sm:text-2xl font-bold" style={{ color: '#1B2A4A' }} data-testid="tdb-title">The Doggy Bakery — Cost Module</h1>
        <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
          Recipe BOM with 15% food-cost target · Shopify product sync · Invoice OCR scanner
        </p>
      </div>

      <div className="border-b overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex gap-1.5 pb-2">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 whitespace-nowrap px-4 py-2 text-sm rounded-full transition-all shrink-0 ${tab === t.id ? 'font-semibold text-white shadow-sm' : 'font-medium text-[#6B7280] hover:bg-[#F7F5F2]'}`}
                style={tab === t.id ? { backgroundColor: '#2A7B88' } : {}}
                data-testid={`tdb-tab-${t.id}`}>
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'intelligence' && <TDBCostIntelligence />}
      {tab === 'cost-entries' && <TDBCostEntriesTab />}
      {tab === 'by-city' && <TDBCostByCityTab />}
      {tab === 'products' && <ProductsTab />}
      {tab === 'pnl' && <MonthlyPnLTab />}
      {tab === 'invoices' && <InvoicesTab />}
    </div>
  );
}

// ============================================================
// PRODUCTS + RECIPE EDITOR
// ============================================================
function ProductsTab() {
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 48;

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, p, c] = await Promise.all([
        api.get('/tdb/food-cost-summary'),
        api.get(`/tdb/products?search=${encodeURIComponent(search)}&category=${encodeURIComponent(filterCat)}&status=${filterStatus}&limit=1000`),
        api.get('/tdb/categories'),
      ]);
      setSummary(s.data);
      setProducts(p.data);
      setCategories(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    const t = setTimeout(() => { loadAll(); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search, filterCat, filterStatus]);

  const runSync = async () => {
    if (syncing) return;
    if (!window.confirm('Pull latest products from thedoggybakery.com? Existing recipes are preserved.')) return;
    setSyncing(true);
    try {
      const r = await api.post('/tdb/shopify/sync');
      alert(`Sync complete: ${r.data.new} new, ${r.data.updated} updated.`);
      await loadAll();
    } catch (e) {
      alert('Sync failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSyncing(false);
  };

  const totalPages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const pageProducts = products.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const rangeStart = products.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, products.length);

  return (
    <div className="space-y-4" data-testid="products-tab">
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
          <SumCard label="Products" value={summary.total_products} color="#378ADD" raw />
          <SumCard label="On target ≤15%" value={summary.buckets.green} color="#2D8B5E" raw />
          <SumCard label="Amber >15%" value={summary.buckets.amber} color="#BA7517" raw />
          <SumCard label="Red >20%" value={summary.buckets.red} color="#D4726A" raw />
          <SumCard label={`Avg food-cost`} value={`${summary.avg_food_cost_pct}%`} color="#1B2A4A" raw />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..."
            className="w-full pl-9 pr-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}
            data-testid="product-search-input" />
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="filter-category">
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="filter-status">
          <option value="">All statuses</option>
          <option value="unset">Recipe pending</option>
          <option value="green">On target</option>
          <option value="amber">Amber</option>
          <option value="red">Red</option>
        </select>
        <button onClick={runSync} disabled={syncing}
          className="flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium hover:bg-[#F7F5F2] disabled:opacity-50"
          style={{ borderColor: '#E5E1DC', color: '#2A7B88' }} data-testid="shopify-sync-btn">
          <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing...' : 'Sync from Shopify'}
        </button>
      </div>

      <CsvToolbar
        labelExport="Export recipes"
        labelImport="Import recipes"
        labelTemplate="Template"
        downloadUrl="/tdb/recipes/download-csv"
        templateUrl="/tdb/recipes/csv-template"
        uploadUrl="/tdb/recipes/upload-csv"
        onImported={loadAll}
      />

      {loading ? (
        <div className="py-10 text-center text-xs" style={{ color: '#9CA3AF' }}>Loading products...</div>
      ) : products.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No products. Click "Sync from Shopify" to fetch the catalog.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-xs" style={{ color: '#6B7280' }}>
            <p data-testid="result-count">
              Showing <strong>{rangeStart}–{rangeEnd}</strong> of {products.length}
              {(search || filterCat || filterStatus) && <span className="ml-1 italic">(filtered)</span>}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded hover:bg-[#F7F5F2] disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="page-prev">
                  <ChevronLeft size={14} />
                </button>
                <span className="text-[11px]" data-testid="page-indicator">
                  Page <strong>{page}</strong> / {totalPages}
                </span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="p-1.5 rounded hover:bg-[#F7F5F2] disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="page-next">
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pageProducts.map(p => (
              <ProductCard key={p.id} product={p} onOpen={() => setActiveProduct(p)} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-2.5 py-1 text-[11px] rounded hover:bg-[#F7F5F2] disabled:opacity-30"
                data-testid="page-first">« First</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-2.5 py-1 text-[11px] rounded hover:bg-[#F7F5F2] disabled:opacity-30">
                ‹ Prev
              </button>
              <span className="text-[11px] px-3" style={{ color: '#1B2A4A' }}>
                <strong>{page}</strong> / {totalPages}
              </span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-2.5 py-1 text-[11px] rounded hover:bg-[#F7F5F2] disabled:opacity-30">
                Next ›
              </button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2.5 py-1 text-[11px] rounded hover:bg-[#F7F5F2] disabled:opacity-30"
                data-testid="page-last">Last »</button>
            </div>
          )}
        </>
      )}

      {activeProduct && (
        <RecipeEditorModal product={activeProduct} onClose={() => setActiveProduct(null)} onSaved={() => { setActiveProduct(null); loadAll(); }} />
      )}
    </div>
  );
}

function ProductCard({ product, onOpen }) {
  const s = STATUS_COLORS[product.food_cost_status] || STATUS_COLORS.unset;
  return (
    <button onClick={onOpen} className="bg-white rounded-xl border overflow-hidden text-left hover:shadow-md transition-shadow"
      style={{ borderColor: '#E5E1DC' }} data-testid={`product-card-${product.id}`}>
      {product.image_url ? (
        <img src={product.image_url} alt={product.title} className="w-full h-28 object-cover" />
      ) : (
        <div className="w-full h-28 flex items-center justify-center" style={{ backgroundColor: '#F7F5F2' }}>
          <Package size={24} style={{ color: '#D4D4D8' }} />
        </div>
      )}
      <div className="p-3 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium truncate flex-1" style={{ color: '#1B2A4A' }}>{product.title}</p>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap"
            style={{ backgroundColor: s.bg, color: s.text }}>
            {product.food_cost_status === 'unset' ? 'No recipe' : `${product.food_cost_pct}%`}
          </span>
        </div>
        <div className="flex items-center justify-between text-[11px]" style={{ color: '#6B7280' }}>
          <span>{product.category_override || product.product_type}</span>
          <span className="font-semibold" style={{ color: '#1B2A4A' }}>{fC(product.selling_price)}</span>
        </div>
        {product.food_cost_status !== 'unset' && (
          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
            <div className="h-full rounded-full" style={{
              width: `${Math.min(product.food_cost_pct / 30 * 100, 100)}%`, backgroundColor: s.accent,
            }} />
          </div>
        )}
      </div>
    </button>
  );
}

function RecipeEditorModal({ product, onClose, onSaved }) {
  const [ingredients, setIngredients] = useState(
    (product.ingredients && product.ingredients.length > 0)
      ? product.ingredients.map(i => ({ ...i }))
      : [{ name: '', quantity: '', unit: 'kg', unit_cost: '', notes: '' }]
  );
  const [sellingPrice, setSellingPrice] = useState(product.selling_price || 0);
  const [categoryOverride, setCategoryOverride] = useState(product.category_override || '');
  const [notes, setNotes] = useState(product.recipe_notes || '');
  const [saving, setSaving] = useState(false);

  const total = ingredients.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_cost) || 0), 0);
  const fcPct = sellingPrice > 0 ? (total / sellingPrice) * 100 : 0;
  const status = fcPct === 0 ? 'unset' : fcPct <= 15 ? 'green' : fcPct <= 20 ? 'amber' : 'red';
  const s = STATUS_COLORS[status];

  const addRow = () => setIngredients(rows => [...rows, { name: '', quantity: '', unit: 'kg', unit_cost: '', notes: '' }]);
  const update = (idx, patch) => setIngredients(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const remove = (idx) => setIngredients(rows => rows.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/tdb/products/${product.id}/recipe`, {
        ingredients,
        selling_price: Number(sellingPrice) || 0,
        category_override: categoryOverride,
        recipe_notes: notes,
      });
      onSaved();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl max-w-3xl w-full my-4 overflow-hidden" data-testid="recipe-modal">
        <div className="flex items-start gap-3 p-4 border-b" style={{ borderColor: '#E5E1DC' }}>
          {product.image_url && <img src={product.image_url} alt="" className="w-16 h-16 object-cover rounded" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }} data-testid="recipe-product-title">{product.title}</p>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>{product.product_type} · Shopify price {fC(product.selling_price)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-lg leading-none">✕</button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MiniMetric label="Total Cost" value={fC(total)} accent="#1B2A4A" />
            <MiniMetric label="Selling" value={fC(sellingPrice)} accent="#378ADD" />
            <MiniMetric label="Food Cost %" value={`${fcPct.toFixed(1)}%`} accent={s.accent} />
            <MiniMetric label="Status" value={s.label} accent={s.accent} raw />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Selling Price *</label>
              <input type="number" value={sellingPrice} onChange={e => setSellingPrice(e.target.value)}
                className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="selling-price-input" />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Category (override)</label>
              <input value={categoryOverride} onChange={e => setCategoryOverride(e.target.value)}
                placeholder={product.product_type}
                className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="category-override-input" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Ingredients / BOM</p>
              <button onClick={addRow} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border hover:bg-[#F7F5F2]"
                style={{ borderColor: '#E5E1DC', color: '#2A7B88' }} data-testid="add-ingredient-btn">
                <Plus size={11} /> Add
              </button>
            </div>
            <div className="space-y-1.5">
              <div className="grid grid-cols-12 gap-2 text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>
                <div className="col-span-4">Ingredient</div>
                <div className="col-span-2">Qty</div>
                <div className="col-span-2">Unit</div>
                <div className="col-span-2">₹/unit</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-1" />
              </div>
              {ingredients.map((r, i) => {
                const rowTotal = (Number(r.quantity) || 0) * (Number(r.unit_cost) || 0);
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`ingredient-row-${i}`}>
                    <input value={r.name} onChange={e => update(i, { name: e.target.value })} placeholder="e.g. Cake flour"
                      className="col-span-4 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
                    <input type="number" value={r.quantity} onChange={e => update(i, { quantity: e.target.value })}
                      className="col-span-2 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
                    <input value={r.unit} onChange={e => update(i, { unit: e.target.value })} placeholder="kg / g / unit"
                      className="col-span-2 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
                    <input type="number" value={r.unit_cost} onChange={e => update(i, { unit_cost: e.target.value })}
                      className="col-span-2 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
                    <span className="col-span-1 text-xs text-right" style={{ color: '#1B2A4A' }}>{fC(rowTotal)}</span>
                    <button onClick={() => remove(i)} className="col-span-1 p-1 hover:bg-red-50 rounded flex justify-center">
                      <Trash2 size={12} style={{ color: '#D4726A' }} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Recipe Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="Baking method / storage / allergens..."
              className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} />
          </div>
        </div>

        <div className="flex gap-2 p-4 border-t" style={{ borderColor: '#E5E1DC' }}>
          <button onClick={save} disabled={saving}
            className="flex-1 px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: '#2A7B88' }} data-testid="save-recipe-btn">
            {saving ? 'Saving...' : 'Save Recipe'}
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// INVOICE SCANNER
// ============================================================
function InvoicesTab() {
  const [scanning, setScanning] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const loadEntries = async () => {
    const r = await api.get('/tdb/cost-entries');
    setEntries(r.data || []);
  };
  useEffect(() => { loadEntries(); }, []);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { alert('File too large (max 15MB).'); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post('/tdb/invoice/scan', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setExtracted({ ...r.data.extracted });
    } catch (err) {
      alert('Scan failed: ' + (err?.response?.data?.detail || err.message));
    }
    setScanning(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const saveEntry = async () => {
    setSaving(true);
    try {
      await api.post('/tdb/invoice/save', {
        ...extracted,
        matched_vendor_id: extracted.matched_vendor?.id || '',
      });
      setExtracted(null);
      await loadEntries();
    } catch (err) {
      alert('Save failed: ' + (err?.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this cost entry?')) return;
    await api.delete(`/tdb/cost-entries/${id}`);
    loadEntries();
  };

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Scan supplier invoice</p>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              Upload JPEG / PNG / WEBP / PDF — Gemini 2.5 Flash reads the vendor, items, amounts, GST. Confirm, save, done.
            </p>
          </div>
          <label className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-white cursor-pointer disabled:opacity-50"
            style={{ backgroundColor: '#2A7B88' }}>
            <Upload size={13} />
            {scanning ? 'Scanning with Gemini...' : 'Upload invoice'}
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleFile}
              className="hidden" disabled={scanning} data-testid="invoice-upload-input" />
          </label>
        </div>
      </div>

      {extracted && (
        <InvoicePreview extracted={extracted} setExtracted={setExtracted} onSave={saveEntry} onCancel={() => setExtracted(null)} saving={saving} />
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Recent cost entries ({entries.length})</p>
          <a href="#" onClick={async (e) => {
              e.preventDefault();
              const token = localStorage.getItem('access_token');
              const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tdb/invoices/download-csv`,
                { headers: { Authorization: `Bearer ${token}` } });
              const blob = await resp.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = `tdb_invoices_${Date.now()}.csv`;
              a.click(); URL.revokeObjectURL(url);
            }}
            className="text-[11px] flex items-center gap-1 hover:underline"
            style={{ color: '#2A7B88' }} data-testid="export-invoices-btn">
            <Download size={11} /> Export CSV
          </a>
        </div>
        {entries.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-sm" style={{ color: '#9CA3AF' }}>No entries yet. Upload an invoice above.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
                <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Date</th>
                <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Vendor</th>
                <th className="text-left px-3 py-2 text-xs font-medium hidden sm:table-cell" style={{ color: '#6B7280' }}>Invoice #</th>
                <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Total</th>
                <th className="text-center px-3 py-2 text-xs font-medium hidden sm:table-cell" style={{ color: '#6B7280' }}>Items</th>
                <th className="text-right px-3 py-2 text-xs font-medium"></th>
              </tr></thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {entries.map(e => (
                  <tr key={e.id} data-testid={`entry-row-${e.id}`}>
                    <td className="px-3 py-2 text-xs" style={{ color: '#1B2A4A' }}>{e.invoice_date || '—'}</td>
                    <td className="px-3 py-2">
                      <p className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{e.vendor_name}</p>
                      {e.vendor_id ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold inline-flex items-center gap-0.5" style={{ backgroundColor: '#ECFDF5', color: '#065F46' }}>
                          <CheckCircle2 size={8} /> linked
                        </span>
                      ) : (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>unlinked</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] hidden sm:table-cell" style={{ color: '#6B7280' }}>{e.invoice_number || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold" style={{ color: '#1B2A4A' }}>{fC(e.grand_total)}</td>
                    <td className="px-3 py-2 text-center text-xs hidden sm:table-cell" style={{ color: '#6B7280' }}>{(e.line_items || []).length}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => del(e.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`delete-entry-${e.id}`}>
                        <Trash2 size={12} style={{ color: '#D4726A' }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoicePreview({ extracted, setExtracted, onSave, onCancel, saving }) {
  const updateItem = (idx, patch) => {
    setExtracted(e => ({
      ...e,
      line_items: e.line_items.map((it, i) => i === idx ? { ...it, ...patch } : it),
    }));
  };
  const removeItem = (idx) => {
    setExtracted(e => ({ ...e, line_items: e.line_items.filter((_, i) => i !== idx) }));
  };
  const addItem = () => {
    setExtracted(e => ({
      ...e,
      line_items: [...(e.line_items || []), { description: '', quantity: 1, unit: '', unit_price: 0, total: 0, hsn: '' }],
    }));
  };

  return (
    <div className="bg-white rounded-xl border p-4 space-y-4" style={{ borderColor: '#2A7B88' }} data-testid="invoice-preview">
      <div className="flex items-start gap-2">
        <CheckCircle2 size={16} style={{ color: '#2D8B5E' }} className="mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Gemini extracted this — review & confirm</p>
          <p className="text-[11px]" style={{ color: '#6B7280' }}>
            Edit any field before saving. {extracted.matched_vendor
              ? (<span style={{ color: '#065F46' }}>Auto-linked to vendor <strong>{extracted.matched_vendor.name}</strong>.</span>)
              : (<span style={{ color: '#92400E' }}>No vendor match found — saved as unlinked.</span>)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <InputField label="Vendor" value={extracted.vendor_name || ''} onChange={v => setExtracted(e => ({ ...e, vendor_name: v }))} testid="preview-vendor" />
        <InputField label="GSTIN" value={extracted.vendor_gstin || ''} onChange={v => setExtracted(e => ({ ...e, vendor_gstin: v }))} />
        <InputField label="Invoice #" value={extracted.invoice_number || ''} onChange={v => setExtracted(e => ({ ...e, invoice_number: v }))} />
        <InputField label="Date" type="date" value={extracted.invoice_date || ''} onChange={v => setExtracted(e => ({ ...e, invoice_date: v }))} />
        <InputField label="Subtotal" type="number" value={extracted.subtotal || 0} onChange={v => setExtracted(e => ({ ...e, subtotal: Number(v) }))} />
        <InputField label="GST" type="number" value={extracted.gst_amount || 0} onChange={v => setExtracted(e => ({ ...e, gst_amount: Number(v) }))} />
        <InputField label="Grand Total" type="number" value={extracted.grand_total || 0} onChange={v => setExtracted(e => ({ ...e, grand_total: Number(v) }))} testid="preview-total" />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Line Items ({(extracted.line_items || []).length})</p>
          <button onClick={addItem} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border hover:bg-[#F7F5F2]"
            style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}>
            <Plus size={11} /> Add
          </button>
        </div>
        <div className="space-y-1.5">
          {(extracted.line_items || []).map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`preview-line-${i}`}>
              <input value={it.description} onChange={e => updateItem(i, { description: e.target.value })}
                className="col-span-5 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <input type="number" value={it.quantity} onChange={e => updateItem(i, { quantity: Number(e.target.value) })}
                className="col-span-2 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <input type="number" value={it.unit_price} onChange={e => updateItem(i, { unit_price: Number(e.target.value) })}
                className="col-span-2 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <input type="number" value={it.total} onChange={e => updateItem(i, { total: Number(e.target.value) })}
                className="col-span-2 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <button onClick={() => removeItem(i)} className="col-span-1 p-1 hover:bg-red-50 rounded flex justify-center">
                <Trash2 size={12} style={{ color: '#D4726A' }} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-2 border-t" style={{ borderColor: '#E5E1DC' }}>
        <button onClick={onSave} disabled={saving}
          className="flex-1 px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: '#2A7B88' }} data-testid="confirm-invoice-btn">
          {saving ? 'Saving...' : 'Confirm & save cost entry'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Discard</button>
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, type = 'text', testid }) {
  return (
    <div>
      <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{label}</label>
      <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid={testid} />
    </div>
  );
}

function SumCard({ label, value, color, raw }) {
  return (
    <div className="bg-white rounded-xl p-3 border" style={{ borderColor: '#E5E1DC' }}>
      <div className="w-6 h-[3px] rounded-sm mb-2" style={{ backgroundColor: color }} />
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className={`${raw ? 'text-xl' : 'text-sm'} font-bold mt-1`} style={{ color: '#1B2A4A' }}>{value}</p>
    </div>
  );
}

function MiniMetric({ label, value, accent, raw }) {
  return (
    <div className="rounded-lg p-2.5 border" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAFAF8' }}>
      <p className="text-[9px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className={`${raw ? 'text-sm' : 'text-base'} font-bold`} style={{ color: accent }}>{value}</p>
    </div>
  );
}

// ============================================================
// MONTHLY P&L — city × month operating costs + full cascade
// ============================================================
function MonthlyPnLTab() {
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState('Bengaluru');
  const [month, setMonth] = useState(defaultMonth);
  const [pnl, setPnl] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [categories, setCategories] = useState([]);
  const [lines, setLines] = useState([{ category: '', amount: '', notes: '' }]);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState('');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [citiesR, catsR] = await Promise.all([
          api.get('/tdb/cities'),
          api.get('/tdb/cost-categories'),
        ]);
        if (!alive) return;
        setCities(citiesR.data || []);
        setCategories(catsR.data || []);
      } catch (e) {
        console.error('[PnL] cities/categories load failed', e);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const reloadPnL = async () => {
    if (!city || !month) return;
    try {
      const [pRes, lbRes] = await Promise.all([
        api.get(`/tdb/pnl?city=${encodeURIComponent(city)}&month_ym=${month}`),
        api.get(`/tdb/pnl/all-cities?month_ym=${month}`),
      ]);
      setPnl(pRes.data);
      setLeaderboard(lbRes.data);
      const existing = pRes.data?.operating_lines || [];
      setLines(existing.length ? existing.map(l => ({ ...l })) : [{ category: '', amount: '', notes: '' }]);
    } catch (e) {
      console.error('[PnL] reload failed', e);
    }
  };

  useEffect(() => {
    if (!city || !month) return;
    let alive = true;
    const load = async () => {
      try {
        const [pRes, lbRes] = await Promise.all([
          api.get(`/tdb/pnl?city=${encodeURIComponent(city)}&month_ym=${month}`),
          api.get(`/tdb/pnl/all-cities?month_ym=${month}`),
        ]);
        if (!alive) return;
        setPnl(pRes.data);
        setLeaderboard(lbRes.data);
        const existing = pRes.data?.operating_lines || [];
        setLines(existing.length ? existing.map(l => ({ ...l })) : [{ category: '', amount: '', notes: '' }]);
      } catch (e) {
        console.error('[PnL] pnl load failed', e);
      }
    };
    load();
    return () => { alive = false; };
  }, [city, month]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/tdb/monthly-costs', { city, month_ym: month, lines });
      await reloadPnL();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const updateLine = (idx, patch) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setLines(ls => [...ls, { category: '', amount: '', notes: '' }]);
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));

  const addCategory = async () => {
    if (!newCat.trim()) return;
    await api.post('/tdb/cost-categories', { name: newCat.trim() });
    setNewCat('');
    const r = await api.get('/tdb/cost-categories');
    setCategories(r.data);
  };

  return (
    <div className="space-y-4" data-testid="pnl-tab">
      {/* ── Leader Cockpit · MIS-truth view at the top of the P&L tab.
           Shipped 18-May-2026. Sits ABOVE the legacy manual P&L editor
           so leaders see the authoritative monthly numbers first, then
           can scroll down to inspect or augment the manual layer. ── */}
      <TDBLeaderCockpit />

      <div className="pt-6 border-t border-[#E5E1DC]">
        <h3 className="text-base font-semibold text-[#1B2A4A] mb-1">Manual P&L Editor (legacy)</h3>
        <p className="text-xs text-[#6B7280] mb-3">
          The cockpit above is the source of truth. This editor stays for ad-hoc
          per-category entries and for testing Aditya's invoice-scanner BOM-driven
          views side-by-side with MIS truth.
        </p>
      </div>
      {/* City / Month pickers */}
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
        <div className="flex gap-1 flex-wrap">
          {cities.map(c => (
            <button key={c} onClick={() => setCity(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${city === c ? 'text-white shadow-sm' : 'text-[#6B7280] hover:bg-[#F7F5F2]'}`}
              style={city === c ? { backgroundColor: '#1B2A4A' } : { border: '1px solid #E5E1DC' }}
              data-testid={`city-pill-${c}`}>
              {c}
            </button>
          ))}
        </div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="px-3 py-1.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}
          data-testid="month-picker" />
      </div>

      {/* P&L cascade */}
      {pnl && (
        <div className="bg-white rounded-xl border p-4 space-y-1" style={{ borderColor: '#E5E1DC' }} data-testid="pnl-cascade">
          <div className="flex items-center justify-between pb-2 border-b" style={{ borderColor: '#F3F4F6' }}>
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{city} · {formatMonth(month)}</p>
            {!pnl.has_bom && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                BOM pending — ingredient cost estimated at 0
              </span>
            )}
          </div>
          <PLine label="Revenue" value={pnl.revenue} bold />
          <PLine label={`− Ingredient cost (≈ ${pnl.avg_food_cost_pct}% avg from BOM)`} value={pnl.ingredient_cost} sub pct={pnl.ingredient_pct} negative />
          <PLine label="= Gross Profit" value={pnl.gross_profit} bold pct={pnl.gross_margin_pct} accent="#2D8B5E" />
          {pnl.operating_lines.map((l, i) => (
            <PLine key={i} label={`− ${l.category}${l.notes ? ` (${l.notes})` : ''}`} value={l.amount} sub negative />
          ))}
          {pnl.operating_lines.length === 0 && (
            <p className="text-xs italic py-2" style={{ color: '#9CA3AF' }}>No operating costs entered for this month yet. Add below.</p>
          )}
          <PLine label="Operating Total" value={pnl.operating_total} bold pct={pnl.operating_pct} accent="#D4726A" negative />
          <PLine label="= Net Contribution" value={pnl.net_contribution}
            big accent={pnl.net_contribution >= 0 ? '#2D8B5E' : '#D4726A'}
            pct={pnl.net_margin_pct} />
        </div>
      )}

      {/* City leaderboard */}
      {leaderboard && (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }} data-testid="city-leaderboard">
          <div className="px-4 py-2 border-b" style={{ borderColor: '#F3F4F6', backgroundColor: '#FAFAF8' }}>
            <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>City leaderboard — {formatMonth(month)}</p>
          </div>
          <table className="w-full text-sm">
            <thead><tr>
              <th className="text-left px-3 py-2 text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>City</th>
              <th className="text-right px-3 py-2 text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Revenue</th>
              <th className="text-right px-3 py-2 text-[10px] font-medium uppercase hidden sm:table-cell" style={{ color: '#9CA3AF' }}>Gross</th>
              <th className="text-right px-3 py-2 text-[10px] font-medium uppercase hidden sm:table-cell" style={{ color: '#9CA3AF' }}>OpEx</th>
              <th className="text-right px-3 py-2 text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Net</th>
              <th className="text-right px-3 py-2 text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Margin</th>
            </tr></thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {leaderboard.rows.map(r => (
                <tr key={r.city} data-testid={`leaderboard-${r.city}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => setCity(r.city)} className="text-sm font-medium hover:underline" style={{ color: '#1B2A4A' }}>{r.city}</button>
                    {!r.costs_entered && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>opex pending</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-xs" style={{ color: '#1B2A4A' }}>{fC(r.revenue)}</td>
                  <td className="px-3 py-2 text-right text-xs hidden sm:table-cell" style={{ color: '#2D8B5E' }}>{fC(r.gross_profit)}</td>
                  <td className="px-3 py-2 text-right text-xs hidden sm:table-cell" style={{ color: '#D4726A' }}>{fC(r.operating_total)}</td>
                  <td className="px-3 py-2 text-right text-xs font-semibold" style={{ color: r.net_contribution >= 0 ? '#1B2A4A' : '#D4726A' }}>{fC(r.net_contribution)}</td>
                  <td className="px-3 py-2 text-right text-xs font-semibold" style={{ color: r.net_margin_pct >= 20 ? '#2D8B5E' : r.net_margin_pct >= 5 ? '#BA7517' : '#D4726A' }}>{r.net_margin_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Operating cost editor */}
      <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="opex-editor">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Operating costs — {city} {formatMonth(month)}</p>
          <button onClick={addLine} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border hover:bg-[#F7F5F2]"
            style={{ borderColor: '#E5E1DC', color: '#2A7B88' }} data-testid="add-opex-row">
            <Plus size={11} /> Add line
          </button>
        </div>
        <div className="space-y-1.5">
          <div className="grid grid-cols-12 gap-2 text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>
            <div className="col-span-4">Category</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-5">Notes</div>
            <div className="col-span-1" />
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`opex-row-${i}`}>
              <input list="tdb-cat-list" value={l.category} onChange={e => updateLine(i, { category: e.target.value })}
                placeholder="e.g. Rent"
                className="col-span-4 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <input type="number" value={l.amount} onChange={e => updateLine(i, { amount: e.target.value })}
                className="col-span-2 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <input value={l.notes || ''} onChange={e => updateLine(i, { notes: e.target.value })}
                className="col-span-5 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <button onClick={() => removeLine(i)} className="col-span-1 p-1 hover:bg-red-50 rounded flex justify-center">
                <Trash2 size={12} style={{ color: '#D4726A' }} />
              </button>
            </div>
          ))}
          <datalist id="tdb-cat-list">
            {categories.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>

        <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: '#F3F4F6' }}>
          <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Add new category (optional)"
            className="flex-1 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid="new-category-input" />
          <button onClick={addCategory} className="text-[11px] px-2 py-1.5 rounded-md border hover:bg-[#F7F5F2]" style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}>
            Add category
          </button>
        </div>

        <button onClick={save} disabled={saving}
          className="w-full px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: '#2A7B88' }} data-testid="save-opex-btn">
          {saving ? 'Saving...' : 'Save operating costs'}
        </button>
      </div>

      <CsvToolbar
        labelExport="Export all months"
        labelImport="Import costs"
        labelTemplate="Template"
        downloadUrl="/tdb/monthly-costs/download-csv"
        templateUrl="/tdb/monthly-costs/csv-template"
        uploadUrl="/tdb/monthly-costs/upload-csv"
        onImported={reloadPnL}
      />
    </div>
  );
}

function PLine({ label, value, sub, bold, big, negative, pct, accent }) {
  const amt = fC(value);
  return (
    <div className={`flex items-center justify-between ${big ? 'pt-2 mt-2 border-t' : ''}`} style={big ? { borderColor: '#E5E1DC' } : {}}>
      <p className={`${sub ? 'text-xs pl-4' : big ? 'text-base' : 'text-sm'} ${bold ? 'font-semibold' : ''}`}
        style={{ color: sub ? '#6B7280' : accent || '#1B2A4A' }}>{label}</p>
      <p className={`${big ? 'text-lg' : sub ? 'text-xs' : 'text-sm'} ${bold || big ? 'font-bold' : ''}`}
        style={{ color: accent || (negative ? '#6B7280' : '#1B2A4A') }}>
        {amt}{typeof pct === 'number' && pct !== 0 ? <span className="ml-2 text-[10px] opacity-75">({pct}%)</span> : null}
      </p>
    </div>
  );
}

function formatMonth(ym) {
  if (!ym || ym.length !== 7) return ym;
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

// CSV Import/Export toolbar — used across all TDB Cost sub-tabs to satisfy
// the "Rule 1" requirement (CRUD + Import/Export available on every module).
function CsvToolbar({ labelExport, labelImport, labelTemplate, downloadUrl,
                     templateUrl, uploadUrl, onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const authedFetch = async (path) => {
    const token = localStorage.getItem('access_token');
    return fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };
  const download = async (path, filename) => {
    const r = await authedFetch(path);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const doUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !uploadUrl) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post(uploadUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = r.data;
      const msg = Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.length : v}`).join('\n');
      alert(`Import complete.\n${msg}`);
      onImported?.();
    } catch (err) {
      alert('Import failed: ' + (err?.response?.data?.detail || err.message));
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="flex flex-wrap items-center gap-2 pt-3 border-t" style={{ borderColor: '#F3F4F6' }} data-testid="csv-toolbar">
      <span className="text-[10px] uppercase font-medium tracking-wider" style={{ color: '#9CA3AF' }}>Data:</span>
      {labelExport && downloadUrl && (
        <button onClick={() => download(downloadUrl, `${downloadUrl.split('/').pop()}_${Date.now()}.csv`)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11px] hover:bg-[#F7F5F2]"
          style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
          data-testid={`csv-export-${labelExport.replace(/\s+/g, '-').toLowerCase()}`}>
          <Download size={11} /> {labelExport}
        </button>
      )}
      {labelTemplate && templateUrl && (
        <button onClick={() => download(templateUrl, 'template.csv')}
          className="flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11px] hover:bg-[#F7F5F2]"
          style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
          <FileText size={11} /> {labelTemplate}
        </button>
      )}
      {labelImport && uploadUrl && (
        <label className="flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11px] cursor-pointer hover:bg-[#F7F5F2]"
          style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}>
          <Upload size={11} /> {busy ? 'Importing...' : labelImport}
          <input ref={fileRef} type="file" accept=".csv" onChange={doUpload} className="hidden" disabled={busy}
            data-testid={`csv-import-${labelImport.replace(/\s+/g, '-').toLowerCase()}`} />
        </label>
      )}
    </div>
  );
}
